import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useExamSessionStore } from '../../../../../stores/StoreContext';
import { Popover, View } from '@instructure/ui';
import { useFullScreenMountNode } from '../../../../../contexts/FullScreenContext';
// Import our modular components
import AnnotationViewer from '../Viewer/AnnotationViewer';

const AnnotationPopover = forwardRef((props, ref) => {
  // State management
  const ManagingAnnotation = useExamSessionStore(
    (state) => state.managingAnnotation
  );
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState('center');
  const [arrowOffset, setArrowOffset] = useState(0);
  const positionElementRef = useRef(null);
  const positionCalculated = useRef(false);
  const popoverRef = useRef(null);
  
  // Get the appropriate mount node for full screen support
  const mountNode = useFullScreenMountNode();

  // Create a debounce function to limit how often we update position
  const debounce = (func, delay) => {
    let timerId;
    return (...args) => {
      if (timerId) clearTimeout(timerId);
      timerId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Expose position and methods through ref
  useImperativeHandle(
    popoverRef,
    () => ({
      position,
      arrowPosition,
      arrowOffset,
      updatePosition: (element) => updatePosition(element),
      getPositionElement: () => positionElementRef.current,
    }),
    [position, arrowPosition, arrowOffset]
  );

  // Function to update position based on element
  const updatePosition = (element) => {
    if (!element) return;

    const rect = element.getBoundingClientRect();

    // Calculate the center point of the element for better arrow alignment
    const elementCenter = rect.left + rect.width / 2;

    setPosition({
      top: rect.bottom,
      left: elementCenter + arrowOffset, // Offset by half the arrow width to center it
    });

    // Update positioning element if it exists
    if (positionElementRef.current) {
      if (popoverRef.current.arrowPosition === 'start') {
        positionElementRef.current.style.left = `${elementCenter - 19}px`;
      } else if (popoverRef.current.arrowPosition === 'end') {
        positionElementRef.current.style.left = `${elementCenter + 20}px`;
      } else {
        positionElementRef.current.style.left = `${elementCenter + arrowOffset}px`;
      }

      if (ManagingAnnotation.type.type === 'point') {
        positionElementRef.current.style.top = `${rect.bottom - 8}px`;
      } else {
        positionElementRef.current.style.top = `${rect.bottom - 6}px`;
      }
    } else {
      // Create positioning element if it doesn't exist
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.left = `${elementCenter - 12}px`;
      div.style.top = `${rect.bottom}px`;
      div.style.width = '1px';
      div.style.height = '1px';
      div.style.visibility = 'hidden';
      document.body.appendChild(div);
      positionElementRef.current = div;
    }
  };

  // Create debounced version of the update function
  const debouncedUpdatePosition = debounce(updatePosition, 50); // 50ms delay for smoother updates

  // Set up position tracking when annotation changes
  useEffect(() => {
    if (!ManagingAnnotation) return;

    const elementId = `annotation_${ManagingAnnotation.sid}`;
    let element = document.getElementById(elementId);

    if (!element) return;

    // Initial position update
    updatePosition(element);
    positionCalculated.current = true;

    // Set up observers for element movement
    const resizeObserver = new ResizeObserver(() => {
      element = document.getElementById(elementId);
      if (element) updatePosition(element); // Use direct update for resize events
    });

    const mutationObserver = new MutationObserver((mutations) => {
      element = document.getElementById(elementId);
      if (element) updatePosition(element); // Use direct update for mutations
    });

    // Listen for scroll and other events that might affect position
    const handlePositionChange = () => {
      element = document.getElementById(elementId);
      if (element) updatePosition(element);
    };

    // Specific handler for window resize
    const handleResize = () => {
      element = document.getElementById(elementId);
      if (element) updatePosition(element);
    };

    // Observe element for size changes
    if (element) {
      resizeObserver.observe(element);
    }

    // Also observe the window for size changes
    resizeObserver.observe(document.documentElement);

    // Observe the entire document for changes that might affect our element
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['style', 'class', 'width', 'height'],
    });

    // Add various event listeners
    document.addEventListener('scroll', handlePositionChange, {
      passive: true,
    });
    window.addEventListener('resize', handleResize, { passive: true });
    document.addEventListener('mousemove', handlePositionChange, {
      passive: true,
    }); // In case dragging affects position
    document.addEventListener('touchmove', handlePositionChange, {
      passive: true,
    });

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      document.removeEventListener('scroll', handlePositionChange);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousemove', handlePositionChange);
      document.removeEventListener('touchmove', handlePositionChange);
      positionCalculated.current = false;
    };
  }, [ManagingAnnotation]);

  // Clean up the DOM element when component unmounts
  useEffect(() => {
    return () => {
      if (positionElementRef.current && positionElementRef.current.parentNode) {
        document.body.removeChild(positionElementRef.current);
        positionElementRef.current = null;
      }
    };
  }, []);

  if (!ManagingAnnotation || !positionElementRef.current) {
    return null;
  }

  return (
    <Popover
      isShowingContent={true}
      placement="bottom"
      elementRef={(el) => {
        popoverRef.current = el;
      }}
      shouldContainFocus
      shouldReturnFocus
      shouldCloseOnDocumentClick={false} // Set to false to prevent closing when interacting with content
      mountNode={() => mountNode} // Mount to full screen container when in full screen mode
      constrain="window"
      positionTarget={positionElementRef.current}
      offsetY={5}
      onPositionChanged={(position) => {
        // position.placement comes in as "bottom start" etc. So we need the second part of that. 
        const placement = position.placement;
        const placementParts = placement.split(' ');
        if(placementParts.length > 1){
          setArrowPosition(placementParts[1]);
        }
      }}
      style={{
        zIndex: 1000,
        background: 'white',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
      }}
    >
      <View as="div" padding="x-small">
        <AnnotationViewer
          annotation={ManagingAnnotation}
          top={position.top}
          left={position.left}
          setShowContextMenu={() => {
            // Handle closing the popover by setting managingAnnotation to null
            const ExamSessionStore = useExamSessionStore.getState();
            ExamSessionStore.setManagingAnnotation(null);
          }}
        />
      </View>
    </Popover>
  );
});

export default AnnotationPopover;
